/**
 * Who you are, across page loads and across worlds.
 *
 * The token is what lets the relay hand a reconnecting player their old slot
 * instead of a second body. localStorage can throw outright in a sandboxed
 * iframe (Discord Activities), so every path falls back to memory — a reconnect
 * within the session still works; a full reload gets a fresh slot.
 */

const TOKEN_KEY = "snowflow-token";
const NAME_KEY = "snowflow-name";
/** Per-room team pick, so a reconnect rejoins the side it chose. */
const TEAM_KEY = (code) => `snowflow-team-${code}`;
/** Which worm hide this player wears. Not per-room: it is a taste, not a seat. */
const SKIN_KEY = "snowflow-worm-skin";

let memoryToken = null;
let memoryName = null;
let memorySkin = null;
const memoryTeams = new Map();

/** No 0/O/1/I lookalikes — these get read aloud and typed in by hand. */
const CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

export function getPlayerToken() {
  try {
    let token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      token = crypto.randomUUID();
      localStorage.setItem(TOKEN_KEY, token);
    }
    return token;
  } catch {
    return (memoryToken ??= crypto.randomUUID());
  }
}

export function getPlayerName() {
  if (memoryName) return memoryName;
  try {
    return localStorage.getItem(NAME_KEY) || defaultName();
  } catch {
    return defaultName();
  }
}

export function setPlayerName(name) {
  memoryName = name;
  try {
    localStorage.setItem(NAME_KEY, name);
  } catch {
    // memory fallback already set
  }
}

/**
 * The team a player picked in a room, remembered for the tab session.
 *
 * sessionStorage rather than localStorage on purpose: a reconnect within the
 * session should not reset a worm to human, but a fresh tab is a fresh seat
 * and gets to pick its own side. Like localStorage it can throw in a
 * sandboxed iframe, so the same memory fallback applies.
 */
export function getRoomTeam(code) {
  if (memoryTeams.has(code)) return memoryTeams.get(code);
  try {
    return sessionStorage.getItem(TEAM_KEY(code));
  } catch {
    return null;
  }
}

export function setRoomTeam(code, team) {
  if (team) memoryTeams.set(code, team);
  else memoryTeams.delete(code);
  try {
    if (team) sessionStorage.setItem(TEAM_KEY(code), team);
    else sessionStorage.removeItem(TEAM_KEY(code));
  } catch {
    // memory fallback already set
  }
}

/**
 * The worm hide this player last chose.
 *
 * localStorage rather than sessionStorage, unlike the team: which side you
 * take is a decision about one match, but what your worm looks like is a
 * preference, and being asked to re-pick it every tab is the annoying kind of
 * stateless. Null when nothing has been picked — callers fall back to the
 * default skin.
 */
export function getWormSkin() {
  if (memorySkin) return memorySkin;
  try {
    return localStorage.getItem(SKIN_KEY);
  } catch {
    return null;
  }
}

export function setWormSkin(skin) {
  memorySkin = skin;
  try {
    localStorage.setItem(SKIN_KEY, skin);
  } catch {
    // memory fallback already set
  }
}

function defaultName() {
  return `Player${Math.floor(Math.random() * 900 + 100)}`;
}

/** Six characters is short enough to say out loud, long enough not to collide. */
export function generateRoomCode() {
  const bytes = crypto.getRandomValues(new Uint8Array(6));
  let code = "";
  for (const b of bytes) code += CODE_ALPHABET[b % CODE_ALPHABET.length];
  return code;
}

/**
 * A side asked for in the link: `?team=worm` (or `human`).
 *
 * Sticky in a way a click is not. A pick made in the lobby lives in
 * sessionStorage and dies with the tab, and the relay's bench only holds a
 * dropped player's side for a grace window — so a refresh late in a match, or
 * one in a fresh tab, used to come back with no side at all. A side in the URL
 * survives all of that, because the URL is what a refresh reloads.
 *
 * It also makes testing two sides two links rather than two sets of clicks,
 * which is what it is mostly for.
 *
 * @returns {"worm"|"human"|null}
 */
export function teamFromUrl() {
  const raw = new URLSearchParams(location.search).get("team");
  if (!raw) return null;
  const team = raw.trim().toLowerCase();
  return team === "worm" || team === "human" ? team : null;
}

/**
 * Write the picked side back into the address bar, without navigating.
 *
 * So that a refresh — the case that lost a side entirely — reloads a link that
 * still names it, and so the address bar is a shareable "join as a worm" link
 * after one click rather than something to hand-edit. replaceState rather than
 * pushState: this is not somewhere to go Back to. It can throw in a sandboxed
 * iframe, where the URL is not ours to write; the pick still stands for this
 * tab either way.
 *
 * @param {"worm"|"human"|null} team
 */
export function setTeamInUrl(team) {
  try {
    const url = new URL(location.href);
    if (team) url.searchParams.set("team", team);
    else url.searchParams.delete("team");
    history.replaceState(history.state, "", url);
  } catch {
    // Not ours to write — sessionStorage still remembers the pick.
  }
}

/** Room code from the URL, normalised. Null when this is not a room link. */
export function roomCodeFromUrl() {
  const raw = new URLSearchParams(location.search).get("room");
  if (!raw) return null;
  const code = raw.toUpperCase();
  return /^[A-Z0-9]{4,10}$/.test(code) ? code : null;
}
